import React, { useEffect, useState } from 'react';
import { ArrowLeft, Download, FileText, BookOpen, FileSpreadsheet, BarChart3, Loader2, CheckCircle2, Clock, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '../utils/config';
import logger from '../utils/logger';

const CATEGORY_ICONS = {
  Whitepapers: FileText,
  Guides: BookOpen,
  Templates: FileSpreadsheet,
  'Research Reports': BarChart3,
  eBooks: BookOpen,
  Checklists: FileSpreadsheet,
  Toolkits: FileSpreadsheet,
  'Case Studies': BarChart3,
};

const ResourceDetail = ({ resourceId, onBack }) => {
  const { t, i18n } = useTranslation();
  const isTR = i18n.language === 'tr';

  const [resource, setResource] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    company: '',
    optIn: false,
    consent: false,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const lang = i18n.language || 'en';
        const r = await fetch(apiUrl(`resources/${resourceId}?lang=${lang}`));
        if (!r.ok) throw new Error('Resource not found');
        const data = await r.json();
        if (!cancelled) setResource(data);
      } catch (e) {
        logger.error('Resource load failed:', e);
        toast.error(isTR ? 'Kaynak yüklenemedi' : 'Could not load resource');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [resourceId, i18n.language, isTR]);

  const updateField = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error(isTR ? 'Lütfen veri kullanım onayını işaretleyin' : 'Please tick the data consent checkbox');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        resource_id: resource.id,
        resource_title: resource.title,
        name: `${form.firstName} ${form.lastName}`.trim(),
        first_name: form.firstName,
        last_name: form.lastName,
        job_title: form.jobTitle,
        email: form.email,
        company: form.company,
        marketing_opt_in: form.optIn,
      };
      const r = await fetch(apiUrl('resource-downloads'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('Submission failed');

      toast.success(isTR ? 'Teşekkürler! İndirme başlatılıyor…' : 'Thanks! Starting download…');
      setSubmitted(true);

      // Trigger the actual download
      const fileUrl = resource.file_url || resource.fileUrl;
      if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = `${resource.title}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      logger.error('Download submit failed:', err);
      toast.error(isTR ? 'Bir hata oluştu, lütfen tekrar deneyin.' : 'Something went wrong, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
        <p className="text-gray-700">{isTR ? 'Kaynak bulunamadı' : 'Resource not found'}</p>
        <Button onClick={onBack} variant="outline">
          {isTR ? 'Kaynaklara Dön' : 'Back to Resources'}
        </Button>
      </div>
    );
  }

  const Icon = CATEGORY_ICONS[resource.category] || FileText;
  const readingMinutes = Math.max(5, Math.round((resource.pages || 12) * 0.9));
  // Build "what you'll learn" bullets from description sentences (fallback to three placeholders)
  const learnPoints = (resource.description || '')
    .split(/(?<=\.)\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 12)
    .slice(0, 3);
  while (learnPoints.length < 3) {
    learnPoints.push(isTR ? 'Profesyonel uygulamada ölçeklenebilir öğrenimler' : 'Scalable, evidence-backed takeaways you can apply tomorrow');
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Slim top nav */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={onBack}
            data-testid="resource-detail-back"
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isTR ? 'Kaynaklara Dön' : 'Back to Resources'}
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 lg:py-20">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          {/* LEFT — content */}
          <div className="lg:col-span-3 space-y-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-blue-600 font-semibold">
              <Icon className="w-4 h-4" />
              {resource.category || (isTR ? 'Kaynak' : 'Resource')}
            </div>

            <h1
              data-testid="resource-detail-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.05] tracking-tight"
            >
              {resource.title}
            </h1>

            <p className="text-lg text-gray-700 leading-relaxed">
              {resource.description}
            </p>

            {resource.image && (
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src={resource.image} alt={resource.title} className="w-full h-auto" loading="lazy" />
              </div>
            )}

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {isTR ? 'Ne öğreneceksiniz' : "What you'll learn"}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                <Clock className="w-4 h-4" />
                <span>{readingMinutes} min</span>
                {resource.pages > 0 && (
                  <>
                    <span aria-hidden="true">•</span>
                    <span>{resource.pages} {t('pages.resources.pages', isTR ? 'sayfa' : 'pages')}</span>
                  </>
                )}
              </div>
              <ul className="space-y-4">
                {learnPoints.map((p, i) => (
                  <li key={`learn-${i}`} className="flex gap-3 text-gray-800 leading-relaxed">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-gray-700 italic border-l-4 border-blue-600 pl-5">
              {isTR
                ? 'Rehberi indirin ve organizasyonunuzdaki liderlik boşluklarını keşfedin.'
                : 'Download the guide and uncover the gaps that are limiting performance in your organization.'}
            </p>
          </div>

          {/* RIGHT — gated form */}
          <div className="lg:col-span-2 lg:sticky lg:top-6">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-7 py-6 text-white">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  {isTR ? 'Kaynağa erişin' : 'Get access to the resource'}
                </h3>
                <p className="text-blue-100 text-sm mt-1">
                  {isTR
                    ? 'Anında indirmek için bilgilerinizi doldurun.'
                    : 'Fill in your details for instant access.'}
                </p>
              </div>

              {submitted ? (
                <div className="p-7 text-center" data-testid="resource-form-success">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {isTR ? 'Teşekkür ederiz!' : 'Thank you!'}
                  </h4>
                  <p className="text-gray-600 mb-5 text-sm">
                    {isTR
                      ? 'İndirme başladı. Bir kopya da e-postanıza gönderildi.'
                      : 'Your download has started. A copy has also been emailed to you.'}
                  </p>
                  {(resource.file_url || resource.fileUrl) && (
                    <a
                      href={resource.file_url || resource.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-semibold"
                    >
                      <Download className="w-4 h-4" />
                      {isTR ? 'Manuel olarak yeniden indir' : 'Download again manually'}
                    </a>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-7 space-y-4" data-testid="resource-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        {isTR ? 'Ad' : 'First name'} *
                      </label>
                      <Input value={form.firstName} onChange={updateField('firstName')} required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        {isTR ? 'Soyad' : 'Last name'} *
                      </label>
                      <Input value={form.lastName} onChange={updateField('lastName')} required />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {isTR ? 'İş E-postası' : 'Business email'} *
                    </label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={updateField('email')}
                      required
                      placeholder="you@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {isTR ? 'Pozisyon' : 'Job title'} *
                    </label>
                    <Input value={form.jobTitle} onChange={updateField('jobTitle')} required />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {isTR ? 'Şirket Adı' : 'Company name'} *
                    </label>
                    <Input value={form.company} onChange={updateField('company')} required />
                  </div>

                  <div className="pt-2 space-y-3 border-t border-gray-100">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.optIn}
                        onChange={updateField('optIn')}
                        className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-600 leading-snug">
                        {isTR
                          ? 'CoachMeUpp ürünleri ve etkinlikleri hakkında pazarlama bilgilerini almak istiyorum.'
                          : 'I want to receive marketing information and event updates from CoachMeUpp.'}
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={updateField('consent')}
                        required
                        className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-600 leading-snug">
                        {isTR
                          ? 'Talep ettiğim içeriği sağlamak amacıyla kişisel verilerimin CoachMeUpp tarafından saklanmasına ve işlenmesine izin veriyorum. *'
                          : 'I agree to allow CoachMeUpp to store and process my personal data to provide me with the content requested. *'}
                      </span>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="resource-form-submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-base py-6 mt-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isTR ? 'Gönderiliyor…' : 'Submitting…'}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {isTR ? 'Şimdi İndir' : 'Download Now'}
                      </>
                    )}
                  </Button>

                  <p className="text-[11px] text-gray-400 leading-snug">
                    {isTR ? (
                      <>İstediğiniz zaman abonelikten çıkabilirsiniz.</>
                    ) : (
                      <>You can unsubscribe at any time. See our privacy practices for details.</>
                    )}
                  </p>
                </form>
              )}
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-5">
              <Briefcase className="w-5 h-5 text-blue-600 mb-2" />
              <p className="text-sm text-gray-700">
                {isTR
                  ? 'Kurumsal bir koçluk programı mı düşünüyorsunuz? Ekibimiz size özel bir teklif hazırlayabilir.'
                  : 'Considering a corporate coaching program? Our team can put together a tailored proposal.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetail;
